import { useParams } from "react-router-dom";
import { fetchGet, fetchPost } from "../util/helpers";
import { useEffect, useState, useContext } from "react";
import Box from '@mui/material/Box';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import { FacebookEmbed, InstagramEmbed, TikTokEmbed, YouTubeEmbed } from "react-social-media-embed";
import { Stack, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import UserContext from "../contexts/UserContext";
import toast from "react-hot-toast";
import { IconPlus } from "@tabler/icons-react";

function CreatorLiveContent() {
    const { campaignId } = useParams();
    const { user } = useContext(UserContext);
    const [postLinkList, setPostLinkList] = useState([]);
    const [addLinkDialogOpen, setAddLinkDialogOpen] = useState(false);
    const [newPostLink, setNewPostLink] = useState("");
    const [newPostPlatform, setNewPostPlatform] = useState("instagram");

    const getMyLiveContent = async () => {
        const results = await fetchGet(
            `/brand/deliverables?campaignId=${campaignId}&deliveredBy=${user.userDocId}`,
        );
        if (results.result) {
            const postLinks = [];
            results.result.forEach((item) => {
                // Only get posts that have social media links (not drafts or concepts)
                if (item.platform !== "draft" && item.platform !== "concept" && item.link && 
                    (item.link.includes("instagram") || item.link.includes("facebook") || 
                     item.link.includes("youtube") || item.link.includes("tiktok"))) {
                    postLinks.push({ ...item });
                }
            });
            setPostLinkList([...postLinks]);
        }
    };

    useEffect(() => {
        if (campaignId && user.userDocId) {
            getMyLiveContent();
        }
    }, [campaignId, user.userDocId]);

    const handleSubmitLink = async () => {
        if (!newPostLink.trim()) {
            toast.error("Please enter a valid URL");
            return;
        }
        
        try {
            await fetchPost(`/brand/deliverables`, {
                campaignId: campaignId,
                platform: newPostPlatform,
                link: newPostLink,
                deliveredBy: user.userDocId
            });
            toast.success("Live content link submitted successfully!");
            setAddLinkDialogOpen(false);
            setNewPostLink("");
            setNewPostPlatform("instagram");
            await getMyLiveContent();
        } catch (error) {
            toast.error("Failed to submit link");
        }
    };

    const renderContent = () => {
        if (postLinkList.length === 0) {
            return (
                <Stack direction="column" spacing={2} sx={{ alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
                    <Typography variant="h4" color="var(--main-text-color)">No Live Content Yet</Typography>
                    <Typography variant="bodysm" color="var(--body-text-color-600)">
                        Your posted content will appear here once you share the live post links with the brand.
                    </Typography>
                </Stack>
            );
        }
        
        return (
            <>
                <Typography variant="h3" color="var(--main-text-color)" sx={{ marginBottom: "20px", marginTop: "20px" }}>
                    My Posted Content
                </Typography>
                <ImageList variant="masonry" cols={3} gap={8}>
                    {postLinkList.filter(item => item.link).map((item) => {
                        const link = item.link;
                        if (link.includes("instagram")) {
                            return (
                                <ImageListItem key={link}>
                                    <InstagramEmbed url={link} width="100%" captioned />
                                </ImageListItem>
                            );
                        } else if (link.includes("facebook")) {
                            return (
                                <ImageListItem key={link}>
                                    <FacebookEmbed url={link} width="100%" captioned />
                                </ImageListItem>
                            );
                        } else if (link.includes("youtube")) {
                            return (
                                <ImageListItem key={link}>
                                    <YouTubeEmbed url={link} width="100%" captioned />
                                </ImageListItem>
                            );
                        } else if (link.includes("tiktok")) {
                            return (
                                <ImageListItem key={link}>
                                    <TikTokEmbed url={link} width={325} captioned />
                                </ImageListItem>
                            );
                        }
                        return (
                            <ImageListItem key={link}>
                                {link}
                            </ImageListItem>
                        );
                    })}
                </ImageList>
            </>
        );
    };

    return (
        <Box sx={{ width: '90%', height: '90vh', marginLeft: '5%', padding: "3%" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: "20px" }}>
                <Typography variant="h3" color="var(--main-text-color)">
                    Live Content
                </Typography>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<IconPlus size={18} />}
                    onClick={() => setAddLinkDialogOpen(true)}
                >
                    Submit Live Post URL
                </Button>
            </Stack>
            
            {renderContent()}

            <Dialog open={addLinkDialogOpen} onClose={() => setAddLinkDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Submit Live Post URL</DialogTitle>
                <DialogContent>
                    <Stack direction="column" spacing={2} sx={{ marginTop: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Platform</InputLabel>
                            <Select
                                value={newPostPlatform}
                                label="Platform"
                                onChange={(e) => setNewPostPlatform(e.target.value)}
                            >
                                <MenuItem value="instagram">Instagram</MenuItem>
                                <MenuItem value="facebook">Facebook</MenuItem>
                                <MenuItem value="youtube">YouTube</MenuItem>
                                <MenuItem value="tiktok">TikTok</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Post URL"
                            placeholder="https://..."
                            value={newPostLink}
                            onChange={(e) => setNewPostLink(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddLinkDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSubmitLink} variant="contained" color="primary">
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default CreatorLiveContent;

